/**
 * Arabic. Mirrors en.ts key for key - a test fails the build when the two drift.
 *
 * Domain terms worth a reviewer's eye, since they recur throughout and the whole
 * portal reads oddly if any of them is off:
 *
 *   points            النقاط
 *   earn / redeem     الكسب / الاستبدال
 *   segment (tier)    الشريحة
 *   voucher           القسيمة
 *   cashback          الاسترداد النقدي
 *   ledger            سجل الحركات
 *   tranche (bucket)  الدفعة
 *   refund/reversal   الاسترداد / العكس
 *   campaign          الحملة
 *   gateway           بوابة الرسائل
 *
 * Counts are kept out of the grammar deliberately: Arabic inflects a noun by its
 * count across six forms, and vue-i18n's pluralisation is two-form. Where the
 * distinction matters a screen picks between two explicit keys instead.
 */
export default {
  common: {
    save: 'حفظ',
    saveChanges: 'حفظ التغييرات',
    cancel: 'إلغاء',
    close: 'إغلاق',
    confirm: 'تأكيد',
    edit: 'تعديل',
    remove: 'إزالة',
    delete: 'حذف',
    search: 'بحث',
    refresh: 'تحديث',
    retry: 'إعادة المحاولة',
    exportCsv: 'تصدير CSV',
    exportXlsx: 'تصدير XLSX',
    download: 'تنزيل',
    upload: 'رفع',
    optional: 'اختياري',
    none: 'لا شيء',
    never: 'أبدًا',
    any: 'الكل',
    all: 'الكل',
    yes: 'نعم',
    no: 'لا',
    loading: 'جارٍ التحميل…',
    from: 'من',
    to: 'إلى',
    status: 'الحالة',
    reason: 'السبب',
    note: 'ملاحظة',
    name: 'الاسم',
    phone: 'رقم الهاتف',
    email: 'البريد الإلكتروني',
    segment: 'الشريحة',
    customer: 'العميل',
    points: 'النقاط',
    balance: 'الرصيد',
    discount: 'الخصم',
    orderTotal: 'إجمالي الطلب',
    orderReference: 'مرجع الطلب',
    channel: 'القناة',
    description: 'الوصف',
    date: 'التاريخ',
    when: 'الوقت',
    by: 'بواسطة',
    type: 'النوع',
    value: 'القيمة',
    code: 'الرمز',
    reference: 'المرجع',
    expires: 'تاريخ الانتهاء',
    active: 'نشط',
    inactive: 'غير نشط',
    nobodyYet: 'لا أحد بعد',
    noPermission: 'ليس لديك صلاحية للقيام بذلك.',
    readOnly: 'لديك صلاحية القراءة فقط لهذه الإعدادات.',
    pts: 'نقطة',
    updatedAt: 'آخر تحديث {at}',
  },

  language: {
    label: 'اللغة',
    switchTo: 'التبديل إلى {name}',
  },

  nav: {
    dashboard: 'لوحة المعلومات',
    earn: 'كسب النقاط',
    redeem: 'الاستبدال',
    refunds: 'المرتجعات',
    transactions: 'سجل الحركات',
    customers: 'العملاء',
    customerDetail: 'العميل',
    segments: 'الشرائح',
    bulkImports: 'الرفع الجماعي',
    vouchers: 'القسائم',
    voucherCodes: 'رموز القسائم',
    cashback: 'الاسترداد النقدي',
    campaigns: 'الحملات',
    reports: 'التقارير',
    program: 'البرنامج',
    notifications: 'الإشعارات',
    smsProviders: 'بوابات الرسائل',
    smsLog: 'سجل الرسائل',
    users: 'المستخدمون',
    roles: 'الأدوار',
    noAccess: 'لا توجد صلاحية',
    notFound: 'غير موجود',
    cashier: 'الكاشير',
    counterReport: 'تقرير الكاشير',
    counterSettings: 'إعدادات الكاشير',
    segmentApplications: 'طلبات العضوية',
  },

  groups: {
    Operations: 'العمليات',
    Customers: 'العملاء',
    Marketing: 'التسويق',
    Insight: 'التحليلات',
    Configuration: 'الإعدادات',
  },

  header: {
    closeLabel: 'إغلاق',
    brand: 'Loyalty',
    mainNav: 'الرئيسية',
    dismiss: 'تجاهل',
    menu: 'القائمة',
    manageAccount: 'إدارة الحساب',
    signOut: 'تسجيل الخروج',
    theme: 'المظهر',
    themeLight: 'فاتح',
    themeDark: 'داكن',
    themeSystem: 'حسب النظام',
  },

  status: {
    ACTIVE: 'نشط',
    INACTIVE: 'غير نشط',
    BLOCKED: 'محظور',
    LOCKED: 'مقفل',
    PENDING: 'قيد الانتظار',
    PENDING_OTP: 'بانتظار الرمز',
    PENDING_APPROVAL: 'بانتظار الموافقة',
    PROCESSING: 'قيد المعالجة',
    COMPLETED: 'مكتمل',
    COMPLETED_WITH_ERRORS: 'مكتمل مع أخطاء',
    FAILED: 'فشل',
    CONFIRMED: 'مؤكد',
    APPROVED: 'معتمد',
    REJECTED: 'مرفوض',
    CANCELLED: 'ملغى',
    EXPIRED: 'منتهي الصلاحية',
    SENT: 'مُرسل',
    AVAILABLE: 'متاح',
    ASSIGNED: 'مخصص',
    REDEEMED: 'مستبدل',
    OUT_OF_STOCK: 'نفد المخزون',
    VERIFIED: 'موثق',
    PENDING_CUSTOMER_APPROVAL: 'بانتظار العميل',
    DECLINED: 'مرفوض من العميل',
    REVOKED: 'ملغاة العضوية',
  },

  transactionType: {
    EARN: 'كسب',
    REDEEM: 'استبدال',
    EXPIRE: 'انتهاء صلاحية',
    ADJUST: 'تعديل',
    VOUCHER_REDEEM: 'قسيمة',
    CASHBACK: 'استرداد نقدي',
    REVERSAL: 'عكس',
  },

  table: {
    emptyTitle: 'لا يوجد ما يُعرض',
    emptyHint: 'عدّل عوامل التصفية، أو عد لاحقًا عند وجود نشاط.',
  },

  pagination: {
    range: '{first}–{last} من {total}',
    previous: 'السابق',
    next: 'التالي',
    page: 'صفحة {page} من {pages}',
  },

  fileDrop: {
    prompt: 'أفلت ملفًا هنا، أو انقر للاختيار',
    accepts: 'يقبل {formats}',
  },

  lookup: {
    placeholder: 'رقم هاتف العميل',
    find: 'بحث',
    clear: 'مسح',
    notFound: 'لا يوجد عميل مسجل بهذا الرقم.',
    failed: 'فشل البحث.',
  },

  errors: {
    generic: 'حدث خطأ ما.',
    network: 'تعذّر الوصول إلى الخدمة. تحقق من اتصالك وحاول مرة أخرى.',
    forbidden: 'ليس لديك صلاحية للقيام بذلك.',
    notFound: 'تعذّر العثور على ذلك.',
    conflict: 'هذا يتعارض مع شيء محفوظ بالفعل.',
    tooLarge: 'حجم الملف كبير جدًا.',
    unprocessable: 'لا يمكن تنفيذ ذلك الآن.',
    upstream: 'إحدى الخدمات الخارجية غير متاحة.',
    server: 'حدث خطأ في الخادم.',
    unknown: 'تعذّر إكمال الطلب.',
    sessionExpired: 'انتهت جلستك. جارٍ إعادة تسجيل دخولك.',
  },

  dashboard: {
    fullReport: 'التقرير الكامل',
    createSegment: 'إنشاء شريحة',
    newThisMonth: 'الجدد هذا الشهر',
    newThisMonthHint: 'المسجلون خلال آخر ٣٠ يومًا',
    outstandingPointsHint: 'ما يدين به البرنامج',
    expiringSoonHint: 'فرصة لاستعادة العملاء',
    earnedLast7: 'المكتسبة، آخر ٧ أيام',
    redeemedLast7: 'المستبدلة، آخر ٧ أيام',
    discountGrantedLabel: 'الخصم الممنوح',
    smsDelivery: 'تسليم الرسائل',
    pointsInAndOut: 'النقاط الواردة والصادرة',
    noActivity: 'لا يوجد نشاط بعد.',
    chartLabel: 'النقاط المكتسبة والمستبدلة يوميًا خلال آخر ١٤ يومًا',
    createSegmentHint: 'أنشئ شريحة لتحديد معدلات الكسب والاستبدال.',
    title: 'لوحة المعلومات',
    subtitle: 'نظرة سريعة على البرنامج.',
    totalCustomers: 'العملاء',
    activeCustomers: 'العملاء النشطون',
    newCustomers: 'الجدد خلال ٣٠ يومًا',
    outstandingPoints: 'النقاط غير المستبدلة',
    pointsEarned: 'المكتسبة خلال ٧ أيام',
    pointsRedeemed: 'المستبدلة خلال ٧ أيام',
    discountGranted: 'الخصم خلال ٧ أيام',
    expiringSoon: 'تنتهي خلال ٣٠ يومًا',
    trend: 'حركة النقاط',
    trendHint: 'المكتسبة مقابل المستبدلة، يومًا بيوم.',
    earned: 'المكتسبة',
    redeemed: 'المستبدلة',
    liability: 'الالتزام حسب الشريحة',
    liabilityHint: 'النقاط غير المستبدلة في كل شريحة.',
    seeReports: 'عرض التقارير الكاملة',
    manageSegments: 'إدارة الشرائح',
    noSegments: 'لا توجد شرائح بعد',
    welcome: 'أهلًا بعودتك، {name}.',
    welcomeAnonymous: 'أهلًا بعودتك.',
    last14Days: 'آخر 14 يومًا',
    segmentMeta: '{customers} عميل · {spend} إجمالي الإنفاق',
  },

  earn: {
    noPermission: 'ليس لديك صلاحية منح النقاط.',
    title: 'كسب النقاط',
    subtitle: 'امنح نقاطًا مقابل طلب. المعدل يأتي من شريحة العميل.',
    findCustomer: 'ابحث عن العميل',
    theOrder: 'الطلب',
    orderTotalHint: 'المبلغ الذي أنفقه العميل.',
    orderReferenceHint: 'رقم نقطة البيع أو الفاتورة. يُسجَّل على قيد الحركة.',
    channelHint: 'DINE_IN وDELIVERY وما إلى ذلك. قد تغيّر قاعدة القناة المعدل.',
    award: 'منح النقاط',
    estimate: 'تقدير',
    estimateUnit: 'نقطة',
    estimateHint: 'بمعدل البرنامج {rate} نقطة لكل وحدة. تطبّق الخدمة معدل شريحة العميل وأي مضاعف للقناة، لذا قد يختلف المبلغ الممنوح.',
    belowMinimum: 'هذا الطلب أقل من الحد الأدنى للكسب البالغ {minimum}، لذا لن يمنح شيئًا. يُسجَّل الطلب على أي حال.',
    lastAward: 'آخر منح',
    pointsAwarded: 'النقاط الممنوحة',
    balanceBefore: 'الرصيد قبل',
    balanceAfter: 'الرصيد بعد',
    awarded: 'تم منح {points} نقطة',
    awardedDetail: 'الرصيد الآن {balance}.',
    nothingAwarded: 'لم تُمنح أي نقاط',
    nothingAwardedDetail: 'الطلب أقل من الحد الأدنى للكسب في البرنامج.',
    failed: 'تعذّر منح النقاط',
    failedDetail: 'تعذّر منح النقاط.',
    belowThresholdWarning: 'هذا الطلب أقل من الحد الأدنى للكسب ({minimum})، لذا لن يمنح أي نقاط. ويظل الطلب مسجَّلًا.',
  },

  redeem: {
    resendCode: 'إعادة إرسال الرمز',
    discountGiven: 'الخصم الممنوح',
    newBalance: 'الرصيد الجديد',
    verify: 'تحقق',
    priceIt: 'احسب السعر',
    useMaximum: 'استخدم الحد الأقصى',
    customerPays: 'يدفع العميل',
    sendCustomerCode: 'أرسل رمزًا إلى العميل',
    cannotInitiate: 'يمكنك حساب سعر الخصم دون بدء العملية.',
    confirmAndDeduct: 'تأكيد وخصم',
    codePlaceholder: '······',
    startOver: 'البدء من جديد',
    progress: 'التقدم',
    orderTotalHint: 'ما سيدفعه العميل قبل أي خصم.',
    discountPercent: 'نسبة الخصم',
    discountAmountLabel: 'مبلغ الخصم',
    pointsToSpend: 'النقاط المراد صرفها',
    orderAndChannel: 'مرجع الطلب والقناة',
    orderReferenceHint: 'رقم نقطة البيع أو الفاتورة، يُسجَّل على قيد الحركة.',
    channelHint: 'DINE_IN وDELIVERY وما إلى ذلك. تؤثر على المعدل عند وجود قاعدة.',
    whatItCosts: 'التكلفة',
    enterOrderTotal: 'أدخل إجمالي الطلب لعرض الخصم المتاح.',
    askForCode: 'اطلب الرمز من العميل',
    verificationCode: 'رمز التحقق',
    codeExpiredResend: 'انتهت صلاحية الرمز — أعد إرساله.',
    confirmed: 'تم تأكيد الاستبدال',
    newCustomerButton: 'عميل جديد',
    title: 'الاستبدال',
    subtitle: 'حوّل النقاط إلى خصم على طلب. يؤكد العميل عبر رمز.',
    findCustomer: 'ابحث عن العميل',
    theOrder: 'الطلب',
    howMuch: 'مقدار الخصم',
    byRate: 'كنسبة مئوية',
    byAmount: 'كمبلغ',
    byPoints: 'كنقاط',
    quote: 'احسب العرض',
    quoteTitle: 'العرض',
    discountRate: 'نسبة الخصم',
    discountAmount: 'الخصم',
    pointsToDeduct: 'النقاط المخصومة',
    netPayable: 'صافي المستحق',
    balanceAfter: 'الرصيد بعد',
    withinRange: 'بين {min} و{max} لهذا العميل.',
    notEligible: 'لا يمكن إتمام هذا الاستبدال',
    sendCode: 'إرسال الرمز',
    otpTitle: 'التأكيد مع العميل',
    otpSent: 'أُرسل رمز إلى {phone}.',
    otpLabel: 'الرمز الذي استلمه العميل',
    otpExpires: 'ينتهي خلال {time}',
    otpExpired: 'انتهت صلاحية هذا الرمز. أرسل رمزًا آخر.',
    attemptsLeft: 'يتبقى {count} محاولات',
    resend: 'إرسال رمز آخر',
    resendsLeft: 'يتبقى {count} إعادة إرسال',
    cancel: 'إلغاء هذا الاستبدال',
    confirm: 'تأكيد الاستبدال',
    doneTitle: 'تم الاستبدال',
    doneDetail: 'خصم {discount}. الرصيد الآن {balance}.',
    nextOrder: 'طلب آخر، نفس العميل',
    newCustomer: 'عميل مختلف',
    cancelled: 'أُلغي الاستبدال',
    tierHint: 'تسمح هذه الشريحة بخصم بين {min} و{max} من قيمة الطلب.',
    pointWorth: 'قيمة النقطة الواحدة {amount}.',
    codeSent: 'أُرسل رمز إلى {phone}. لم يُخصم أي شيء بعد.',
    expiresIn: 'ينتهي خلال {time}',
    referenceLabel: 'المرجع {reference}',
  },

  refunds: {
    title: 'المرتجعات',
    subtitle: 'عكس لحركة نقاط سابقة. يتطلب مديرًا، ولا يمكن أن يعتمده من قدّمه.',
    raise: 'تقديم طلب استرداد',
    raised: 'تاريخ الطلب',
    reverses: 'يعكس',
    anEarn: 'عملية كسب',
    aRedemption: 'عملية استبدال',
    yoursToWithdraw: 'طلبك، يمكنك سحبه',
    withdraw: 'سحب',
    approve: 'اعتماد',
    reject: 'رفض',
    emptyTitle: 'لا يوجد شيء هنا',
    emptyHint: 'تظهر هنا طلبات الاسترداد المقدمة من الموظفين لاتخاذ قرار بشأنها.',
    clearCustomer: 'مسح تصفية العميل',
    approveTitle: 'اعتماد هذا الاسترداد',
    rejectTitle: 'رفض هذا الاسترداد',
    withdrawTitle: 'سحب هذا الطلب',
    originalEntry: 'القيد الأصلي',
    pointsMoved: 'النقاط التي يحركها العكس',
    discountReversed: 'الخصم المعكوس',
    raisedBy: 'مقدّم الطلب',
    approveWarning: 'الاعتماد يضيف قيد عكس إلى السجل ويحرّك الرصيد فورًا. قد يصبح الرصيد سالبًا إذا كانت النقاط قد أُنفقت بالفعل.',
    noteHint: 'تُحفظ مع القرار.',
    rejectNeedsReason: 'الرفض يحتاج إلى سبب.',
    approveAndReverse: 'اعتماد وعكس',
    approved: 'تم اعتماد الاسترداد',
    approvedDetail: 'عُكست {points} نقطة. الرصيد الآن {balance}.',
    rejected: 'تم رفض الاسترداد',
    withdrawn: 'تم سحب الطلب',
    decisionFailed: 'تعذّر تسجيل القرار.',
    raiseSubtitle: 'ابحث عن العميل، واختر القيد المراد عكسه، ووضّح السبب.',
    noReversible: 'لا يوجد لدى هذا العميل قيد يمكن عكسه. القيود المعكوسة بالفعل، وقيود العكس نفسها، لا يمكن استردادها مجددًا.',
    loadingEntries: 'جارٍ تحميل القيود القابلة للعكس…',
    cannotReverse: 'لا يمكن عكسه',
    reasonHint: 'سبب إرجاع الطلب أو إلغاء الاستبدال. سيقرأ المدير هذا.',
    sendForApproval: 'إرسال للاعتماد',
    requestRaised: 'تم تقديم طلب الاسترداد',
    requestRaisedDetail: 'في انتظار اعتماد أحد المديرين.',
    requestFailed: 'تعذّر تقديم الطلب.',
    balanceLabel: 'الرصيد {points}',
    noReversibleEntries: 'لا توجد لدى هذا العميل حركة يمكن عكسها. الحركات التي سبق عكسها، وعمليات العكس نفسها، لا يمكن استردادها مرة أخرى.',
    entryOrder: 'الطلب {total}',
    entryDiscount: 'خصم {amount}',
    entryRef: 'مرجع {ref}',
    pairBadge: 'طلب كاشير {reference} — يُقرَّر مع الطلب المرتبط به',
    pairHint: 'هذا الاسترداد والاسترداد المرتبط به يعكسان طلب كاشير واحدًا. اعتماد أيٍّ منهما أو رفضه يقرّر الاثنين.',
    pairDecision: 'هذا الاسترداد أحد زوج لطلب الكاشير {reference}. ينطبق قرارك على الاثنين: تُعكس النقاط المُنفقة والنقاط المكتسبة معًا، أو لا يُعكس أي منهما.',
  },

  transactions: {
    anyType: 'أي نوع',
    title: 'سجل الحركات',
    subtitle: 'كل حركة نقاط، بالترتيب الذي حدثت به. لا تُعدَّل القيود أبدًا — يظهر التصحيح كقيد عكس مستقل.',
    balanceAfter: 'الرصيد بعد',
    order: 'الطلب',
    emptyTitle: 'لا توجد حركات في هذه الفترة',
    emptyHint: 'وسّع نطاق التواريخ، أو امسح تصفية النوع والمرجع.',
    entryTitle: 'قيد السجل',
    entryNumber: 'القيد {id}',
    balanceBefore: 'الرصيد قبل',
    linkedTo: 'مرتبط بـ',
    performedBy: 'نُفذت بواسطة',
    system: 'النظام',
    openCustomer: 'فتح العميل',
    exportFailed: 'فشل التصدير',
  },

  customers: {
    searchLabel: 'البحث في العملاء',
    title: 'العملاء',
    subtitle: 'كل المسجلين في البرنامج. النقاط تخص العميل مباشرة.',
    enrol: 'تسجيل عميل',
    searchPlaceholder: 'الاسم أو الهاتف أو البريد أو المرجع',
    allSegments: 'كل الشرائح',
    anyStatus: 'أي حالة',
    lifetimeSpend: 'إجمالي الإنفاق',
    enrolled: 'تاريخ التسجيل',
    emptyTitle: 'لا يوجد عملاء مطابقون',
    emptyHint: 'امسح عوامل التصفية، أو سجّل أول عميل.',
    enrolTitle: 'تسجيل عميل',
    enrolSubtitle: 'رقم الهاتف هو كل المطلوب؛ وما عداه يمكن إضافته لاحقًا.',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    phoneHint: 'يُستخدم لرمز تأكيد الاستبدال، لذا يجب أن يكون قابلًا للوصول.',
    dateOfBirth: 'تاريخ الميلاد',
    segmentHint: 'اتركه فارغًا لاستخدام الافتراضي في البرنامج.',
    noSegment: 'بدون شريحة',
    enrolled_toast: 'تم تسجيل العميل',
    enrolFailed: 'تعذّر تسجيل العميل.',
    loadFailed: 'تعذّر تحميل العملاء.',
    enrolButton: 'تسجيل',
  },

  customer: {
    adjustPlaceholder: 'مثل 250 أو -250',
    back: '← العملاء',
    notFound: 'هذا العميل غير موجود.',
    loadFailed: 'تعذّر تحميل بيانات العميل.',
    backToList: 'العودة إلى العملاء',
    enrolledOn: 'مسجل منذ {date}',
    pointsBalance: 'رصيد النقاط',
    lifetimeEarned: 'إجمالي المكتسب',
    lifetimeRedeemed: 'إجمالي المستبدل',
    expiringIn30: 'تنتهي خلال ٣٠ يومًا',
    nextOn: 'التالية في {date}',
    profile: 'الملف الشخصي',
    job: 'الوظيفة',
    address: 'العنوان',
    externalRef: 'المرجع الخارجي',
    externalRefHint: 'معرّف هذا العميل في نظامك، إن وُجد.',
    lastActivity: 'آخر نشاط',
    quickActions: 'إجراءات سريعة',
    quickActionsHint: 'خدمة هذا العميل عند نقطة البيع أو عبر الهاتف.',
    awardPoints: 'منح نقاط مقابل طلب',
    redeemAgainst: 'الاستبدال مقابل طلب',
    raiseRefund: 'تقديم طلب استرداد',
    tabLedger: 'سجل الحركات',
    tabBuckets: 'دفعات الانتهاء',
    tabSpend: 'الإنفاق',
    ledgerEmpty: 'لا توجد حركات نقاط بعد',
    ledgerEmptyHint: 'يظهر هنا كل كسب واستبدال وتعديل وعكس.',
    bucketsHint: 'تُنفق النقاط الأقرب لانتهاء الصلاحية أولًا، لذا تُصرف الدفعة العليا أولًا.',
    bucketsEmpty: 'لا توجد دفعات غير منفقة',
    bucketsEmptyHint: 'لا شيء ينتظر انتهاء الصلاحية.',
    earnedAt: 'تاريخ الكسب',
    earnedPoints: 'النقاط المكتسبة',
    remaining: 'المتبقي',
    spendEmpty: 'لا يوجد إنفاق مسجل',
    spendEmptyHint: 'تظهر الطلبات هنا بمجرد كسب نقاط عليها.',
    orders: '{count} طلب',
    editTitle: 'تعديل العميل',
    saved: 'تم تحديث العميل',
    saveFailed: 'تعذّر حفظ التغييرات.',
    segmentTitle: 'تغيير الشريحة',
    segmentSubtitle: 'تحدد الشريحة معدل الكسب ومعدل الاستبدال والحد الأقصى للخصم.',
    chooseSegment: 'اختر شريحة',
    segmentReasonHint: 'يُسجَّل مع التغيير.',
    changeSegment: 'تغيير الشريحة',
    segmentChanged: 'تم تغيير الشريحة',
    segmentChangedDetail: 'أصبح الآن في {segment}.',
    segmentFailed: 'تعذّر تغيير الشريحة',
    statusTitle: 'تغيير الحالة',
    statusHint: 'العميل المحظور لا يمكنه الكسب ولا الاستبدال.',
    apply: 'تطبيق',
    statusChanged: 'أصبح العميل الآن {status}',
    statusFailed: 'تعذّر تغيير الحالة',
    adjustTitle: 'تعديل النقاط',
    adjustSubtitle: 'تصحيح يُسجَّل في السجل كأي حركة أخرى.',
    adjustPointsHint: 'موجب للإضافة، وسالب للخصم.',
    adjustReasonHint: 'يظهر على قيد السجل وفي تقارير التدقيق.',
    adjustExpiry: 'تنتهي خلال (أيام)',
    adjustExpiryHint: 'اتركه فارغًا لاستخدام مدة انتهاء البرنامج.',
    adjustPreview: 'الرصيد {balance}. بعد هذا التعديل سيصبح {after}.',
    postAdjustment: 'تسجيل التعديل',
    adjustAdded: 'تمت إضافة {points} نقطة',
    adjustDeducted: 'تم خصم {points} نقطة',
    adjustFailed: 'فشل التعديل',
    orderCount: '{count} طلب',
    membership: 'العضوية',
    memberSince: 'عضو منذ',
    viaApplication: 'عبر الطلب',
    latestApplication: 'آخر طلب',
    revokeMembership: 'إلغاء العضوية',
    revokeTitle: 'إلغاء هذه العضوية',
    revokeWarning: 'سيعود العميل إلى الشريحة التي كان فيها قبل الموافقة، ويفقد خصم العضوية من طلبه التالي، وسيصله إشعار.',
    revokeHint: 'يُحفظ مع العضوية ويظهر للعميل.',
    membershipRevoked: 'تم إلغاء العضوية',
    membershipRevokedDetail: 'عاد العميل إلى شريحته السابقة.',
  },

  segments: {
    maxDiscountLabel: 'الحد الأقصى للخصم %',
    title: 'الشرائح',
    subtitle: 'كل شريحة تحدد معدل الكسب ومعدل الاستبدال وأكبر خصم يمكن لعميل فيها أن يأخذه على طلب.',
    create: 'شريحة جديدة',
    earnRate: 'معدل الكسب',
    redeemRate: 'معدل الاستبدال',
    maxDiscount: 'الحد الأقصى للخصم',
    orderValue: 'قيمة الطلب',
    customersCount: 'العملاء',
    anyOrder: 'الكل',
    andUp: '{amount} فأكثر',
    upTo: 'حتى {amount}',
    range: '{min} – {max}',
    emptyTitle: 'لا توجد شرائح بعد',
    emptyHint: 'أنشئ واحدة لتحديد شروط الكسب والاستبدال لمجموعة من العملاء.',
    retire: 'إيقاف',
    activate: 'تفعيل',
    createTitle: 'شريحة جديدة',
    editTitle: 'تعديل {title}',
    modalSubtitle: 'تُدخل المعدلات كنسب مئوية من إجمالي الطلب.',
    titleLabel: 'العنوان',
    earnRateHint: 'نقاط لكل وحدة إنفاق.',
    redeemRateHint: 'قيمة كل نقطة عند الاستبدال.',
    maxDiscountHint: 'الحد الأقصى للطلب الواحد. الحد الأدنى هو {floor}%.',
    belowFloor: 'حد أقصى أقل من الحد الأدنى للبرنامج البالغ {floor}% لا يترك أي خصم يمكن لعميل في هذه الشريحة أخذه.',
    minOrderValue: 'الحد الأدنى لقيمة الطلب',
    minOrderHint: 'اتركه فارغًا لعدم وجود حد أدنى.',
    maxOrderValue: 'الحد الأقصى لقيمة الطلب',
    displayOrder: 'ترتيب العرض',
    created: 'تم إنشاء الشريحة',
    updated: 'تم تحديث الشريحة',
    saveFailed: 'تعذّر حفظ الشريحة.',
    retireTitle: 'إيقاف هذه الشريحة؟',
    activateTitle: 'تفعيل هذه الشريحة؟',
    retireMessage: 'يبقى العملاء في {title}، لكن لا يمكن تعيينها بعد الآن وتتوقف شروطها عن الانطباق على النشاط الجديد.',
    activateMessage: 'تصبح {title} قابلة للتعيين مجددًا وتُطبَّق معدلاتها.',
    retired: 'تم إيقاف الشريحة',
    activated: 'تم تفعيل الشريحة',
    changeFailed: 'تعذّر تغيير الشريحة',
    loadFailed: 'تعذّر تحميل الشرائح.',
    belowFloorWarning: 'السقف الأقل من الحد الأدنى للبرنامج ({floor}%) لا يترك أي خصم يمكن لعميل في هذه الشريحة أخذه.',
    memberDiscount: 'خصم العضوية',
    openBadge: 'مفتوحة لطلبات العضوية',
    titleArLabel: 'العنوان بالعربية',
    titleArHint: 'يظهر للعملاء الذين يستخدمون التطبيق بالعربية. العنوان أعلاه هو الإنجليزي.',
    descriptionArLabel: 'الوصف بالعربية',
    membershipHeading: 'العضوية',
    memberDiscountLabel: 'خصم العضوية %',
    memberDiscountHint: 'يُخصم من كل طلب أولًا، قبل العروض وأكواد الخصم والنقاط. اكتب 0 لعدم وجود خصم.',
    memberDiscountInvalid: 'أدخل نسبة بين 0 و100.',
    openForApplications: 'مفتوحة لطلبات العضوية',
    openForApplicationsHint: 'يمكن للعملاء التقدم من التطبيق للانضمام ببطاقة العضوية. تتم مراجعة كل طلب من صفحة طلبات العضوية.',
    openWithoutDiscount: 'هذه الشريحة مفتوحة لطلبات العضوية لكن بدون خصم عضوية. سيحصل الأعضاء المقبولون على معدلات الكسب والاستبدال الخاصة بها فقط.',
  },

  bulk: {
    title: 'الرفع الجماعي',
    subtitle: 'حمّل العملاء والأرصدة وتعيينات الشرائح من جدول بيانات. نزّل ملف المثال أولًا — فهو يحتوي على الأعمدة التي تتوقعها الخدمة بالضبط.',
    customersTitle: 'العملاء',
    customersDescription: 'سجّل قائمة من العملاء. رقم هاتف موجود يحدّث ذلك العميل بدلًا من إنشاء عميل ثانٍ.',
    balancesTitle: 'الأرصدة',
    balancesDescription: 'عدّل الأرصدة جماعيًا. كل صف يسجل قيدًا عاديًا في السجل مع السبب الذي كتبته — لا شيء يُستبدل بصمت.',
    segmentsTitle: 'شرائح العملاء',
    segmentsDescription: 'انقل العملاء بين الشرائح، بعنوان الشريحة أو معرّفها.',
    exampleSheet: 'ملف المثال',
    recentUploads: 'عمليات الرفع الأخيرة',
    allTypes: 'كل الأنواع',
    started: 'البدء',
    file: 'الملف',
    rows: 'الصفوف',
    loaded: 'المحمّلة',
    rejected: 'المرفوضة',
    dryRunBadge: 'تشغيل تجريبي',
    emptyTitle: 'لا توجد عمليات رفع بعد',
    emptyHint: 'تظهر هنا الملفات التي ترفعها مع نتيجة كل صف.',
    uploadTitle: 'رفع {kind}',
    expectedColumns: 'الأعمدة المتوقعة: {columns}',
    dryRun: 'تشغيل تجريبي',
    dryRunHint: 'يتحقق من كل صف ولا يكتب شيئًا. ابدأ بهذا.',
    willWrite: 'هذا سيكتب فعليًا. صفوف الأرصدة تسجل قيودًا حقيقية في السجل، ولا يمكن تعديلها لاحقًا — بل عكسها فقط.',
    validate: 'تحقق',
    uploadAndApply: 'رفع وتطبيق',
    dryRunStarted: 'بدأ التشغيل التجريبي',
    uploadStarted: 'بدأ الرفع',
    progressBelow: 'يظهر التقدم في القائمة أدناه.',
    uploadResult: '{kind} {reference}: قُبل {accepted}، ورُفض {rejected} من {total}.',
    uploadFailed: 'تعذّر رفع الملف.',
    errorsTitle: 'الصفوف المرفوضة',
    row: 'الصف',
    column: 'العمود',
    cellValue: 'القيمة',
    why: 'السبب',
    exportErrors: 'تصدير الصفوف المرفوضة',
    templateFailed: 'تعذّر تنزيل ملف المثال',
    errorsFailed: 'تعذّر تحميل الصفوف المرفوضة',
    exportFailed: 'فشل التصدير',
    loadFailed: 'تعذّر تحميل عمليات الرفع.',
    writeWarning: 'سيُكتب هذا فعليًا. صفوف الأرصدة تُقيَّد في سجل الحركات، ولا يمكن تعديلها بعد ذلك - يمكن عكسها فقط.',
  },

  reports: {
    title: 'التقارير',
    subtitle: 'كل تقرير تنشره الخدمة. عوامل التصفية أدناه تأتي من التقرير نفسه، وكل تقرير يُصدَّر كاملًا — وليس الصفحة المعروضة فقط.',
    catalogue: 'الفهرس',
    run: 'تشغيل التقرير',
    needs: 'يتطلب {fields}.',
    rowsGenerated: '{rows} صف · أُنشئ في {when}',
    noRows: 'لا توجد صفوف',
    noRowsHint: 'لا شيء يطابق هذه المعايير.',
    total: 'الإجمالي',
    totalsNote: 'الإجماليات تشمل كامل النتائج، وليس هذه الصفحة فقط.',
    catalogueFailed: 'تعذّر تحميل فهرس التقارير.',
    runFailed: 'تعذّر تشغيل التقرير.',
    exportFailed: 'فشل التصدير',
    anyOption: 'الكل',
    allSegments: 'كل الشرائح',
    needsParameters: 'يتطلب {names}.',
    resultMeta: '{rows} صف · أُنشئ {at}',
  },

  program: {
    title: 'البرنامج',
    subtitle: 'الإعدادات الافتراضية والحدود القصوى. تحدد الشرائح معدلاتها ضمن هذه الحدود؛ أما الحد الأدنى للخصم وقواعد رمز التأكيد فتنطبق على الجميع.',
    updated: 'حُدّث في {when}',
    identity: 'الهوية',
    displayName: 'الاسم المعروض',
    currency: 'العملة',
    currencyHint: 'تظهر بجانب كل مبلغ في البوابة.',
    code: 'الرمز',
    codeHint: 'ثابت طوال عمر البرنامج.',
    earning: 'الكسب',
    earningHint: 'يُستخدم للعملاء بلا شريحة، وكبديل احتياطي في كل مكان آخر.',
    earnRate: 'معدل الكسب %',
    earnRateHint: 'نقاط لكل وحدة إنفاق.',
    minOrderEarn: 'الحد الأدنى للطلب للكسب',
    minOrderEarnHint: 'الطلبات دون هذا الحد تُسجَّل لكنها لا تمنح شيئًا.',
    expiryDays: 'تنتهي النقاط بعد (أيام)',
    expiryDaysHint: 'الفراغ يعني أن النقاط لا تنتهي أبدًا. تحتفظ الدفعات الحالية بتاريخ الانتهاء الممنوح لها.',
    redeeming: 'الاستبدال',
    redeemingHint: 'الاستبدال هو خصم على طلب. تحدد شريحة العميل الحد الأقصى؛ والحد الأدنى هنا ينطبق على الجميع.',
    redeemRate: 'معدل الاستبدال %',
    redeemRateHint: 'قيمة النقطة الواحدة عند الاستبدال.',
    minOrderRedeem: 'الحد الأدنى للطلب للاستبدال',
    minRedeemPoints: 'الحد الأدنى من النقاط للاستبدال',
    minDiscount: 'الحد الأدنى للخصم %',
    minDiscountHint: 'لا يجوز أن يقل أي استبدال عن هذا، في أي شريحة.',
    maxDiscount: 'الحد الأقصى للخصم %',
    maxDiscountHint: 'الحد الأقصى للعميل بلا شريحة. الحد الخاص بالشريحة له الأولوية.',
    floorAboveCeiling: 'الحد الأدنى أعلى من الحد الأقصى، مما لا يترك أي خصم ممكن لأحد.',
    otp: 'رموز التأكيد',
    otpHint: 'كل استبدال يُؤكَّد برمز يُرسل إلى العميل. تحدد هذه الإعدادات المهلة المتاحة وعدد المحاولات.',
    otpLength: 'طول الرمز',
    otpTtl: 'صالح لمدة (ثوانٍ)',
    otpAttempts: 'المحاولات المسموحة',
    otpAttemptsHint: 'بعدها يُقفل الاستبدال.',
    otpResends: 'إعادات الإرسال المسموحة',
    discard: 'تجاهل التغييرات',
    saveProgram: 'حفظ البرنامج',
    saved: 'تم حفظ البرنامج',
    savedDetail: 'تُطبَّق الإعدادات الجديدة على العملية التالية.',
    saveFailed: 'تعذّر حفظ البرنامج.',
    loadFailed: 'تعذّر تحميل البرنامج.',
    channelRules: 'قواعد القنوات',
    channelRulesHint: 'مضاعف يُطبَّق فوق المعدل للطلبات من قناة معينة — يوم نقاط مضاعفة للتوصيل مثلًا.',
    addRule: 'إضافة قاعدة',
    channel: 'القناة',
    earnMultiplier: 'مضاعف الكسب ×',
    redeemMultiplier: 'مضاعف الاستبدال ×',
    noRules: 'لا توجد قواعد قنوات',
    noRulesHint: 'كل القنوات تكسب وتستبدل بالمعدل القياسي.',
    addRuleTitle: 'إضافة قاعدة قناة',
    editRuleTitle: 'تعديل {channel}',
    channelHint: 'تُطابَق مع القناة المرسلة مع الطلب، مثل DELIVERY.',
    earnMultiplierLabel: 'مضاعف الكسب',
    redeemMultiplierLabel: 'مضاعف الاستبدال',
    ruleAdded: 'تمت إضافة قاعدة القناة',
    ruleUpdated: 'تم تحديث قاعدة القناة',
    ruleSaveFailed: 'تعذّر حفظ القاعدة.',
    removeRuleTitle: 'إزالة قاعدة القناة هذه؟',
    removeRuleMessage: 'ستكسب الطلبات من {channel} وتستبدل بالمعدل القياسي.',
    ruleRemoved: 'تمت إزالة قاعدة القناة',
    ruleRemoveFailed: 'تعذّر إزالة القاعدة',
    minutes: '{minutes} دقيقة',
    seconds: '{seconds} ثانية',
    minutesSeconds: '{minutes} د {seconds} ث',
  },

  notifications: {
    editSubtitle: 'اكتب الرسالة التي يستلمها العميل.',
    title: 'الإشعارات',
    subtitle: 'الأحداث التي تُرسل رسالة إلى العميل، ونص تلك الرسائل. تُملأ العناصر النائبة بين الأقواس عند الإرسال.',
    alwaysOn: 'دائمًا مُفعّل',
    editMessage: 'تعديل الرسالة',
    switchOn: 'تفعيل',
    switchOff: 'إيقاف',
    updatedAt: 'حُدّث في {when}',
    messageLabel: 'الرسالة',
    placeholdersHint: 'العناصر النائبة المتاحة هنا — انقر للإدراج:',
    languageLabel: 'اللغة',
    languageHint: 'يُرسل النص العربي وغيره من النصوص غير اللاتينية بترميز يونيكود، مما يقصّر كل مقطع.',
    preview: 'معاينة',
    previewUpdating: 'جارٍ التحديث…',
    previewEmpty: 'اكتب رسالة لمعاينتها.',
    previewCountOne: '{characters} حرف · مقطع رسالة واحد',
    previewCountMany: '{characters} حرف · {segments} مقاطع رسائل',
    unresolvedOne: '{names} ليس عنصرًا نائبًا يوفره هذا الحدث، لذا سيُرسل كما هو.',
    unresolvedMany: '{names} ليست عناصر نائبة يوفرها هذا الحدث، لذا ستُرسل كما هي.',
    saveMessage: 'حفظ الرسالة',
    switchedOn: 'تم تفعيل الإشعار',
    switchedOff: 'تم إيقاف الإشعار',
    toggleFailed: 'تعذّر تغيير الإشعار',
    messageUpdated: 'تم تحديث الرسالة',
    saveFailed: 'تعذّر حفظ الرسالة.',
    loadFailed: 'تعذّر تحميل الإعدادات.',
    events: {
      REDEMPTION_OTP: 'رمز الاستبدال',
      POINTS_EARNED: 'كسب نقاط',
      POINTS_REDEEMED: 'استبدال نقاط',
      POINTS_ADJUSTED: 'تعديل نقاط',
      POINTS_EXPIRING_SOON: 'نقاط توشك على الانتهاء',
      POINTS_EXPIRED: 'نقاط منتهية',
      CUSTOMER_ENROLLED: 'تسجيل عميل',
      SEGMENT_CHANGED: 'تغيير الشريحة',
      VOUCHER_ISSUED: 'إصدار قسيمة',
      CASHBACK_AWARDED: 'منح استرداد نقدي',
    },
    languages: {
      ENGLISH: 'الإنجليزية',
      ARABIC: 'العربية',
      UNICODE: 'يونيكود',
    },
    previewChars: '{count} حرف',
    previewPartsOne: 'رسالة واحدة',
    previewPartsMany: '{count} رسائل',
  },

  sms: {
    updatedLabel: 'آخر تحديث',
    otpTemplateIdLabel: 'معرّف قالب رمز التأكيد',
    providersTitle: 'بوابات الرسائل',
    providersSubtitle: 'بوابة واحدة نشطة في كل وقت وترسل كل الرسائل — بما فيها رمز تأكيد الاستبدال. تُخزَّن المفاتيح وكلمات المرور للكتابة فقط.',
    addGateway: 'إضافة بوابة',
    noGateway: 'لا توجد بوابة مهيأة',
    noGatewayHint: 'حتى تكون هناك بوابة نشطة ومفعّلة، لن تُرسل أي رسالة — وهو ما يعني أنه لا يمكن تأكيد أي استبدال.',
    activeBadge: 'نشطة',
    standbyBadge: 'احتياطية',
    sender: 'المرسل',
    environment: 'البيئة',
    environmentLive: 'مباشرة',
    environmentTest: 'اختبارية',
    nothingDelivered: '(لا يُسلَّم شيء)',
    otpTemplate: 'قالب رمز التأكيد',
    endpoint: 'نقطة النهاية',
    credentials: 'بيانات الاعتماد',
    apiKeySet: 'مفتاح API محفوظ',
    passwordSet: 'كلمة المرور محفوظة',
    tokenSet: 'الرمز المميز محفوظ',
    sendTest: 'إرسال رسالة اختبار',
    makeActive: 'جعلها نشطة',
    typeSmsMisr: 'SMS Misr',
    typeSmsMisrHint: 'التنفيذ المخصص، بما في ذلك نقطة نهاية رمز التأكيد.',
    typeGeneric: 'HTTP عام',
    typeGenericHint: 'أي بوابة لديها واجهة HTTP، تُهيَّأ أدناه.',
    typeLogging: 'تسجيل فقط',
    typeLoggingHint: 'يكتب الرسالة في سجل الخدمة ولا يرسل شيئًا. للاختبار.',
    addTitle: 'إضافة بوابة',
    editTitle: 'تعديل {name}',
    codeLabel: 'الرمز',
    codeHint: 'معرّف قصير، مثل SMS_MISR_LIVE.',
    nameLabel: 'الاسم',
    typeLabel: 'النوع',
    senderIdLabel: 'معرّف المرسل',
    senderIdHint: 'الاسم الذي تظهر منه الرسالة. يُسجَّل عادةً لدى البوابة.',
    environmentHint: 'البيئة الاختبارية تقبل الطلب ولا تُسلّم شيئًا.',
    otpTemplateHint: 'ترسل SMS Misr رموز التأكيد عبر قالب مسجل مسبقًا.',
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    passwordStored: 'كلمة المرور محفوظة. اتركها فارغة للإبقاء عليها.',
    endpointUrl: 'رابط نقطة النهاية',
    method: 'الطريقة',
    contentType: 'نوع المحتوى',
    contentJson: 'JSON',
    contentForm: 'مُرمّز كنموذج',
    contentPlain: 'نص عادي',
    auth: 'المصادقة',
    authNone: 'بدون',
    authApiKey: 'ترويسة مفتاح API',
    authBasic: 'أساسية',
    authBearer: 'رمز مميز',
    headerName: 'اسم الترويسة',
    headerNameHint: 'مثل X-API-Key.',
    apiKey: 'مفتاح API',
    apiKeyStored: 'المفتاح محفوظ. اتركه فارغًا للإبقاء عليه.',
    bearerToken: 'الرمز المميز',
    bearerTokenStored: 'الرمز محفوظ. اتركه فارغًا للإبقاء عليه.',
    bodyTemplate: 'قالب الجسم',
    bodyTemplateHint: 'استخدم {tokens}. تُستبدل مع كل رسالة.',
    successCodes: 'رموز النجاح',
    successCodesHint: 'رموز مفصولة بفواصل تعيدها البوابة للرسالة المقبولة.',
    saveGateway: 'إضافة بوابة',
    gatewayAdded: 'تمت إضافة البوابة',
    gatewayUpdated: 'تم تحديث البوابة',
    gatewaySaveFailed: 'تعذّر حفظ البوابة.',
    gatewayActivated: 'تم تفعيل البوابة',
    gatewayActivatedDetail: 'تُرسل {name} الآن كل الرسائل.',
    activateFailed: 'تعذّر تفعيل البوابة',
    removeTitle: 'إزالة هذه البوابة؟',
    removeMessage: 'سيتم حذف {name}. تحتفظ الرسائل المرسلة عبرها بسجلها.',
    gatewayRemoved: 'تمت إزالة البوابة',
    removeFailed: 'تعذّر إزالة البوابة',
    testTitle: 'إرسال رسالة اختبار',
    testPhoneHint: 'رقم حقيقي — سيتم الإرسال فعليًا.',
    testAccepted: 'قبلت البوابة الرسالة.',
    testRejected: 'رفضتها البوابة.',
    testId: 'المعرّف {id}',
    sendTestButton: 'إرسال اختبار',
    testFailed: 'تعذّر إرسال الاختبار.',
    gatewaysFailed: 'تعذّر تحميل البوابات.',

    logTitle: 'سجل الرسائل',
    logSubtitle: 'كل رسالة أدرجتها الخدمة في الطابور وما ردّته البوابة. لا تُسجَّل رموز التأكيد — فقط أنه تم إرسال رمز.',
    queued: 'وقت الإدراج',
    toColumn: 'إلى',
    kind: 'النوع',
    message: 'الرسالة',
    tries: 'المحاولات',
    anyStatus: 'أي حالة',
    kindPlaceholder: 'مثل REDEMPTION_OTP',
    failedWarningOne: 'فشلت رسالة واحدة في هذه الصفحة. تحقق من بيانات اعتماد البوابة النشطة ورصيدها قبل إعادة إدراجها.',
    failedWarningMany: 'فشلت {count} رسائل في هذه الصفحة. تحقق من بيانات اعتماد البوابة النشطة ورصيدها قبل إعادة إدراجها.',
    logEmpty: 'لا توجد رسائل في هذه الفترة',
    logEmptyHint: 'وسّع نطاق التواريخ، أو امسح تصفية الحالة والهاتف.',
    messageTitle: 'الرسالة',
    attempts: 'المحاولات',
    gateway: 'البوابة',
    gatewayId: 'معرّف البوابة',
    linkedTo: 'مرتبطة بـ',
    scheduled: 'مجدولة',
    processed: 'تمت معالجتها',
    requeued: 'أُعيد إدراج الرسالة',
    requeuedDetail: 'إلى {phone}.',
    requeueFailed: 'تعذّر إعادة إدراج الرسالة',
    logFailed: 'تعذّر تحميل السجل.',
  },

  users: {
    searchLabel: 'البحث في المستخدمين',
    title: 'المستخدمون',
    subtitle: 'يثبت Keycloak هوية الشخص؛ وهذه القائمة تحدد ما يمكنه فعله. من يسجّل دخوله وليس مدرجًا هنا لا يملك أي صلاحية.',
    invite: 'دعوة مستخدم',
    searchPlaceholder: 'الاسم أو اسم المستخدم أو البريد',
    username: 'اسم المستخدم',
    roles: 'الأدوار',
    lastSignedIn: 'آخر تسجيل دخول',
    you: 'أنت',
    noRole: 'بلا دور — لا يمكنه فعل شيء',
    suspend: 'تعليق',
    reinstate: 'إعادة تفعيل',
    emptyTitle: 'لا يوجد مستخدمون مطابقون',
    emptyHint: 'ادعُ شخصًا، أو امسح عوامل التصفية.',
    inviteTitle: 'دعوة مستخدم',
    editTitle: 'تعديل {name}',
    inviteSubtitle: 'يجب أن يطابق اسم المستخدم حسابه في Keycloak، وإلا فلن يُتعرَّف عليه عند تسجيل الدخول.',
    usernameHint: 'كما يظهر في Keycloak.',
    displayName: 'الاسم المعروض',
    rolesHint: 'تأتي الصلاحيات من الأدوار. من لا دور له يمكنه تسجيل الدخول دون رؤية شيء.',
    noRoleWarning: 'بدون دور سيتمكن هذا المستخدم من تسجيل الدخول دون القدرة على فعل أي شيء.',
    inviteButton: 'دعوة',
    invited: 'تمت دعوة المستخدم',
    invitedDetail: 'يمكن لـ{name} تسجيل الدخول بمجرد امتلاكه حساب Keycloak باسم {username}.',
    updated: 'تم تحديث المستخدم',
    saveFailed: 'تعذّر حفظ المستخدم.',
    suspended: 'تم تعليق المستخدم',
    reinstated: 'تمت إعادة تفعيل المستخدم',
    statusFailed: 'تعذّر تغيير المستخدم',
    loadFailed: 'تعذّر تحميل المستخدمين.',
  },

  roles: {
    title: 'الأدوار',
    subtitle: 'الدور مجموعة من الصلاحيات. تتحقق الخدمة من الصلاحية لا من الدور، ويُطبَّق الفحص نفسه على كل من يستدعيها.',
    create: 'دور جديد',
    builtIn: 'مدمج',
    permissionCountOne: '{held} من {total} صلاحية · مستخدم واحد',
    permissionCountMany: '{held} من {total} صلاحية · {users} مستخدمين',
    andMore: '+{count} أخرى',
    view: 'عرض',
    createTitle: 'دور جديد',
    modalSubtitle: 'حدّد ما يمكن لهذا الدور فعله. كل ما لا يُحدَّد ترفضه الخدمة.',
    codeLabel: 'الرمز',
    codeHint: 'بأحرف كبيرة، مثل CALL_CENTRE_AGENT.',
    descriptionPlaceholder: 'الغرض من هذا الدور',
    systemRoleWarning: 'هذا دور مدمج. يُعرض هنا للاطلاع ولا يمكن تغييره — أنشئ دورًا خاصًا بك لمنح مجموعة مختلفة.',
    created: 'تم إنشاء الدور',
    updated: 'تم تحديث الدور',
    saveFailed: 'تعذّر حفظ الدور.',
    deleteTitle: 'حذف هذا الدور؟',
    deleteHeldOne: '{name} يحمله مستخدم واحد. سيفقد كل صلاحية منحها له هذا الدور.',
    deleteHeldMany: '{name} يحمله {count} مستخدمين. سيفقدون كل صلاحية منحها لهم هذا الدور.',
    deleteUnheld: 'سيتم حذف {name}. لا يحمله أي مستخدم.',
    deleted: 'تم حذف الدور',
    deleteFailed: 'تعذّر حذف الدور',
    loadFailed: 'تعذّر تحميل الأدوار.',
    permissionCount: '{held} من {total} صلاحية',
    userCountOne: 'مستخدم واحد',
    userCountMany: '{count} مستخدمين',
    morePermissions: '+{count} أخرى',
  },

  vouchers: {
    title: 'القسائم',
    subtitle: 'الرمز يساوي مالًا ويمكن صرفه مرة واحدة. تبدأ القسائم الجديدة متوقفة — فعّلها عمدًا قبل أن تتمكن من الإصدار.',
    create: 'قسيمة جديدة',
    countVouchers: 'القسائم',
    countActive: 'النشطة',
    codesOutstanding: 'الرموز القائمة',
    codesOutstandingHint: 'متاحة أو بحوزة عميل',
    codesRedeemed: 'الرموز المستبدلة',
    kind: 'النوع',
    worth: 'القيمة',
    codes: 'الرموز',
    window: 'الفترة',
    free: '{count} متاح',
    held: '{count} محجوز',
    spent: '{count} مصروف',
    mintsOnDemand: 'يُصدر عند الطلب',
    always: 'دائمًا',
    fromDate: 'من {date}',
    untilDate: 'حتى {date}',
    dateRange: '{from} – {to}',
    emptyTitle: 'لا توجد قسائم بعد',
    emptyHint: 'أنشئ واحدة، ثم ولّد رموزًا أو دعها تصدرها عند الطلب.',
    codesButton: 'الرموز',
    switchOff: 'إيقاف',
    activate: 'تفعيل',
    createTitle: 'قسيمة جديدة',
    editTitle: 'تعديل {name}',
    categoryLabel: 'النوع',
    categoryHint: 'قسائم الاسترداد النقدي تنشئها العروض نفسها.',
    categoryStandard: 'قياسية',
    categoryCampaign: 'حملة',
    categoryCashback: 'استرداد نقدي',
    valueTypeLabel: 'نوع القيمة',
    valueTypeFixed: 'مبلغ ثابت',
    valueTypePercentage: 'نسبة من الطلب',
    valuePercent: 'القيمة %',
    valueLabel: 'القيمة',
    valuePercentHint: 'من إجمالي الطلب، بحد أقصى قيمة الطلب كاملة.',
    valueFixedHint: 'لا تتجاوز أبدًا قيمة الطلب.',
    codeExpiryDays: 'تنتهي الرموز بعد (أيام)',
    codeExpiryHint: 'الفراغ يعني أن الرمز يدوم بقدر ما تدوم القسيمة.',
    starts: 'يبدأ',
    ends: 'ينتهي',
    autoStock: 'إصدار الرموز عند الطلب',
    autoStockHint: 'الإيقاف يعني أن الرموز تأتي من مخزون تولّده مسبقًا، وتنفد القسيمة عندما يفرغ ذلك المخزون.',
    createButton: 'إنشاء القسيمة',
    created: 'تم إنشاء القسيمة',
    createdDetail: '{name} متوقفة حتى تفعّلها.',
    updated: 'تم تحديث القسيمة',
    saveFailed: 'تعذّر حفظ القسيمة.',
    activated: 'تم تفعيل القسيمة',
    switchedOff: 'تم إيقاف القسيمة',
    toggleFailed: 'تعذّر تغيير القسيمة',
    generateTitle: 'توليد رموز',
    howMany: 'العدد',
    generateExpiry: 'تنتهي بعد (أيام)',
    generateExpiryHint: 'يتجاوز إعداد القسيمة نفسها لهذه الدفعة.',
    generated: 'تم توليد {count} رمز',
    generatedDetail: 'أُضيف {count} رمز إلى المخزون.',
    generateFailed: 'تعذّر توليد الرموز.',
    generateButton: 'توليد',
    loadFailed: 'تعذّر تحميل القسائم.',
    stockFree: '{count} متاح',
    stockHeld: '{count} محجوز',
    stockSpent: '{count} مستخدم',
    codesAdded: 'أُضيف {count} رمزًا إلى المخزون.',
  },

  voucherCodes: {
    codeLabel: 'رمز القسيمة',
    title: 'رموز القسائم',
    subtitle: 'ابحث عن رمز، واعرف من يحمله، واصرفه على طلب. يمكن استبدال الرمز مرة واحدة.',
    findTitle: 'البحث عن رمز',
    lookUp: 'بحث',
    notFound: 'لا توجد قسيمة بهذا الرمز.',
    lookupFailed: 'فشل البحث.',
    voucher: 'القسيمة',
    worth: 'القيمة',
    holder: 'الحامل',
    issuedBy: 'صادرة بواسطة',
    issued: 'تاريخ الإصدار',
    redeemedAt: 'تاريخ الاستبدال',
    codesTitle: 'الرموز',
    emptyTitle: 'لا توجد رموز',
    emptyHint: 'ولّد دفعة، أو دع القسيمة تصدرها عند الإصدار.',
    redeem: 'استبدال',
    withdraw: 'سحب',
    redeemTitle: 'استبدال رمز قسيمة',
    redeemIntro: 'ابحث عن العميل الذي يُستخدم هذا الرمز من أجله. ترفض الخدمة رمزًا صدر لشخص آخر.',
    orderTotalRequired: 'مطلوب: هذه القسيمة نسبة من الطلب.',
    orderTotalOptional: 'اختياري. الخصم محدود بقيمة الطلب.',
    redeemed: 'خصم {amount}',
    redeemedDetail: 'استُبدلت القسيمة {code} لصالح {customer}.',
    redeemResult: 'خصم {amount} على هذا الطلب. سُجِّل في السجل كقيد {id}.',
    redeemFailed: 'تعذّر استبدال الرمز.',
    withdrawTitle: 'سحب هذا الرمز؟',
    withdrawMessage: 'لن يعود بالإمكان استبدال {code}. الرمز المصروف بالفعل لا يمكن سحبه — قدّم طلب استرداد على قيد السجل بدلًا من ذلك.',
    withdrawn: 'تم سحب الرمز',
    withdrawFailed: 'تعذّر سحب الرمز',
    loadFailed: 'تعذّر تحميل الرموز.',
    redeemLookupHint: 'ابحث عن العميل الذي يُستخدم هذا الرمز له. يرفض النظام أي رمز صادر لشخص آخر.',
  },

  cashback: {
    title: 'الاسترداد النقدي',
    subtitle: 'الطلب المؤهل يمنح العميل قسيمة تلقائيًا، دون أن يفعل أحد شيئًا عند نقطة البيع.',
    create: 'عرض جديد',
    offer: 'العرض',
    givesBack: 'يعيد',
    onOrders: 'على الطلبات',
    segments: 'الشرائح',
    perCustomer: 'لكل عميل',
    window: 'الفترة',
    everyone: 'الجميع',
    noLimit: 'بلا حد',
    times: '{count}×',
    anyOrder: 'أي طلب',
    andUp: '{amount} فأكثر',
    upTo: 'حتى {amount}',
    range: '{min} – {max}',
    always: 'دائمًا',
    fromDate: 'من {date}',
    untilDate: 'حتى {date}',
    dateRange: '{from} – {to}',
    emptyTitle: 'لا توجد عروض استرداد نقدي',
    emptyHint: 'أنشئ واحدًا لإعادة شيء على الطلبات المؤهلة.',
    pause: 'إيقاف مؤقت',
    run: 'تشغيل',
    createTitle: 'عرض استرداد نقدي جديد',
    editTitle: 'تعديل {name}',
    rewardType: 'نوع المكافأة',
    rewardPercentage: 'نسبة من الطلب',
    rewardFixed: 'مبلغ ثابت',
    givesBackPercent: 'يعيد %',
    givesBackLabel: 'يعيد',
    rewardHint: 'تُمنح للعميل كرمز قسيمة.',
    minimumOrder: 'الحد الأدنى للطلب',
    minimumOrderHint: 'دون هذا الحد، لا يُعاد شيء.',
    maximumOrder: 'الحد الأقصى للطلب',
    maximumOrderHint: 'الفراغ يعني عدم وجود حد أعلى.',
    timesPerCustomer: 'عدد المرات لكل عميل',
    timesPerCustomerHint: 'الفراغ يعني كلما استحق العميل ذلك.',
    unbounded: 'هذا العرض بلا حد أقصى للطلب وبلا حد لكل عميل، لذا فإن طلبًا كبيرًا واحدًا يعيد قسيمة كبيرة بالتناسب، وكلما تكرر ذلك.',
    starts: 'يبدأ',
    ends: 'ينتهي',
    voucherExpiry: 'تنتهي القسيمة بعد (أيام)',
    segmentsHint: 'اترك الكل بلا تحديد لتقديم العرض لكل عميل.',
    createButton: 'إنشاء العرض',
    created: 'تم إنشاء العرض',
    updated: 'تم تحديث العرض',
    saveFailed: 'تعذّر حفظ العرض.',
    paused: 'تم إيقاف العرض مؤقتًا',
    running: 'العرض قيد التشغيل',
    toggleFailed: 'تعذّر تغيير العرض',
    loadFailed: 'تعذّر تحميل العروض.',
    unboundedWarning: 'لا حد أقصى للطلب ولا حد لكل عميل في هذا العرض، لذا يمنح الطلب الكبير الواحد قسيمة كبيرة بالتناسب، وكلما تكرر ذلك.',
  },

  campaigns: {
    title: 'الحملات',
    subtitle: 'ابنِ جمهورًا مما فعله العملاء، واطّلع عليه، ثم صدّره أو أرسل له رسالة أو امنح كل فرد فيه قسيمة.',
    audience: 'الجمهور',
    segmentsHint: 'عدم تحديد أي شريحة يعني كل الشرائح.',
    anyStatus: 'أي حالة',
    pointsFrom: 'النقاط من',
    pointsTo: 'النقاط إلى',
    spendFrom: 'الإنفاق من',
    spendTo: 'الإنفاق إلى',
    quietFor: 'خامل لمدة لا تقل عن (أيام)',
    quietForHint: 'العملاء بلا حركة نقاط طوال هذه المدة. من لم يتعامل قط يُحتسب أيضًا، بدءًا من تاريخ تسجيله.',
    birthdayMonth: 'شهر الميلاد',
    anyMonth: 'أي شهر',
    sex: 'النوع',
    sexMale: 'ذكر',
    sexFemale: 'أنثى',
    sexOther: 'آخر',
    reachAtMost: 'الوصول إلى ما لا يزيد عن',
    reachAtMostHint: 'يحد من التنفيذ. يُحتفظ بأصحاب الأرصدة الأكبر عند تقليص الجمهور.',
    preview: 'معاينة الجمهور',
    audienceCount: 'الجمهور',
    pointsHeld: 'النقاط المحتفظ بها',
    pointsHeldHint: 'عبر الجمهور بأكمله',
    lifetimeSpend: 'إجمالي الإنفاق',
    whoThisReaches: 'من ستصل إليه',
    sendSms: 'إرسال رسالة',
    issueVouchers: 'إصدار قسائم',
    noActiveVoucher: 'لا توجد قسيمة نشطة للإصدار. فعّل واحدة أولًا.',
    emptyTitle: 'لا أحد مطابق',
    emptyHint: 'خفّف عوامل التصفية — الحملة التي لا تصل إلى أحد عادةً ما تكون تصفيتها ضيقة جدًا.',
    noAudienceTitle: 'لا يوجد جمهور بعد',
    noAudienceHint: 'اضبط عوامل التصفية ثم عاين. لا يمكن إرسال شيء قبل أن ترى من ستصل إليه.',
    lastActivity: 'آخر نشاط',
    buildFailed: 'تعذّر بناء الجمهور.',
    exportFailed: 'فشل التصدير',
    smsTitle: 'إرسال رسالة إلى هذا الجمهور',
    customerCount: '{count} عميل',
    campaignName: 'اسم الحملة',
    campaignNameHint: 'يُسجَّل مع كل رسالة تُرسل.',
    untitled: 'حملة بلا عنوان',
    messageLabel: 'الرسالة',
    smsTemplateHint: 'استخدم {tokens}. وأي شيء آخر يُرسل كما هو مكتوب.',
    smsWarning: 'هذا يُدرج رسالة إلى {count} عميل ولا يمكن التراجع عنه بعد بدء الإرسال.',
    smsResult: 'أُدرجت {queued} من {total}.',
    skipped: 'تم تخطي {count} — {reason}',
    queueMessages: 'إدراج {count} رسالة',
    messagesQueued: 'أُدرجت {count} رسالة',
    smsFailed: 'تعذّر إدراج الحملة.',
    voucherTitle: 'إصدار قسيمة لهذا الجمهور',
    voucherLabel: 'القسيمة',
    textEachCustomer: 'إرسال رمزه إلى كل عميل برسالة',
    voucherTemplateHint: 'استخدم {tokens}.',
    voucherWarning: 'هذا يصدر رمزًا لكل عميل من {count} عميل. كل رمز يساوي مالًا ولا يمكن إلغاء إصداره — بل سحبه واحدًا تلو الآخر فقط.',
    voucherResult: 'صدر {issued} رمز، وأُدرجت {queued} رسالة.',
    issueCodes: 'إصدار {count} رمز',
    codesIssued: 'صدر {count} رمز',
    voucherFailed: 'تعذّر إصدار القسائم.',
  },

  counter: {
    title: 'الكاشير',
    subtitle: 'ابحث عن العميل، وسجّل الطلب، ثم أدخل الخصم الظاهر هنا في نظام نقاط البيع.',
    callCentre: 'مركز الاتصال',
    posNumber: 'رقم طلب نقطة البيع',
    unassignedTitle: 'لم يتم تعيينك إلى فرع بعد.',
    unassignedMessage: 'اطلب من أحد المسؤولين تعيينك إلى فرعك من إعدادات الكاشير، ثم أعد تحميل هذه الصفحة.',
    identify: {
      title: 'التعرّف على العميل',
      appCode: 'رمز التطبيق',
      phone: 'الهاتف (بدون تطبيق)',
      codeLabel: 'الرمز من تطبيق العميل',
      codeHint: 'امسح رمز QR، أو اكتب الرمز الذي يقرؤه العميل. المسافات لا تهم.',
      phoneHint: 'للعميل الذي لا يملك التطبيق. الكسب لا يحتاج شيئًا آخر؛ أما الإنفاق فيُرسل إليه رمزًا برسالة.',
      find: 'بحث',
    },
    order: {
      title: 'الطلب',
      changeCustomer: 'عميل آخر',
      posNumberHint: 'كما طبعه نظام نقاط البيع تمامًا. لا يمكن تسجيل الطلب الواحد إلا مرة واحدة.',
      orderTotalHint: 'الإجمالي قبل أي خصم بالنقاط.',
      usePoints: 'استخدام النقاط',
      cannotSpend: 'لا يمكن إنفاق النقاط على هذا الطلب.',
      otpNotice: 'سيُرسل رمز برسالة إلى {phone}. يقرؤه العميل للموافقة على الخصم.',
      approvalNotice: 'سيُطلب من العميل الموافقة على هذا الخصم في تطبيق Maky قبل إنفاق أي شيء.',
      discount: 'الخصم',
      discountRange: 'بين {min} و{max} على هذا الطلب.',
      discountOutOfRange: 'يجب أن يكون الخصم بين {min} و{max}.',
      summary: 'الملخص',
      enterTotal: 'أدخل إجمالي الطلب لمعرفة ما يمكن للعميل إنفاقه وكسبه.',
      discountToKey: 'الخصم المطلوب إدخاله في نقطة البيع',
      netPayable: 'يدفع العميل',
      pointsSpent: 'النقاط المُنفقة',
      estimatedEarned: 'النقاط المتوقع كسبها (تقديري)',
      needsPosNumber: 'أدخل رقم طلب نقطة البيع للمتابعة.',
      record: 'تأكيد',
      textCode: 'إرسال رمز برسالة',
      askApproval: 'طلب الموافقة في التطبيق',
      safeToRetry: 'اضغط تأكيد مرة أخرى: لن يُسجَّل الطلب مرتين.',
      memberDiscount: 'خصم العضوية',
      memberDiscountOf: 'خصم العضوية ({detail})',
    },
    result: {
      enterDiscount: 'أدخل هذا الخصم في نقطة البيع',
      noDiscount: 'لا يوجد خصم — كسب نقاط فقط',
      earned: '+{points} نقطة مكتسبة',
      newBalance: 'الرصيد الجديد',
      nextOrder: 'الطلب التالي',
      startAgain: 'البدء من جديد',
      waitingApproval: 'في انتظار موافقة العميل في تطبيق Maky',
      waitingDetail: 'يُطلب منه الموافقة على خصم قدره {discount}. تتحدّث هذه الشاشة تلقائيًا.',
      cancelFailed: 'تعذّر الإلغاء',
      nothingMoved: 'لم يُنفق أو يُكتسب أي شيء. لا تمنح خصمًا على هذا الطلب.',
      declined: 'رفض العميل',
      cancelled: 'تم الإلغاء',
      expired: 'انتهت مهلة الموافقة',
      failed: 'تعذّر تسجيل الطلب',
    },
    otp: {
      sent: 'أُرسل رمز برسالة إلى {phone}. اطلب من العميل قراءته.',
      label: 'الرمز من الرسالة',
      expiresIn: 'ينتهي خلال {time}',
      expired: 'انتهت صلاحية الرمز.',
      resendsLeft: 'متبقٍ {count} مرات إعادة إرسال',
      resend: 'إعادة إرسال الرمز',
      resent: 'أُعيد إرسال الرمز',
    },
    today: {
      title: 'اليوم في هذا الفرع',
      time: 'الوقت',
      earned: 'النقاط المكتسبة',
      emptyTitle: 'لا توجد طلبات اليوم بعد',
      emptyHint: 'تظهر الطلبات المسجّلة هنا في هذه القائمة.',
    },
    reversal: {
      request: 'طلب عكس',
      title: 'طلب عكس الطلب',
      explain: 'يجب أن يعتمد أحد المديرين هذا الطلب في المرتجعات. لا يتغيّر شيء قبل ذلك: تُعاد النقاط المُنفقة وتُسحب النقاط المكتسبة معًا، أو لا يحدث أي منهما.',
      reasonHint: 'سبب عكس الطلب. سيقرأ المدير هذا.',
      raised: 'تم تقديم طلب العكس',
      raisedDetail: 'في انتظار اعتماد أحد المديرين.',
      status: {
        PENDING_APPROVAL: 'العكس قيد الانتظار',
        APPROVED: 'معكوس',
        REJECTED: 'رُفض العكس',
        CANCELLED: 'سُحب طلب العكس',
      },
    },
    errors: {
      codeLocked: 'رموز خاطئة كثيرة. البحث بالرمز مقفل لعشر دقائق — استخدم تبويب الهاتف أو انتظر.',
      codeInvalid: 'هذا الرمز غير صحيح أو منتهي أو مستخدم من قبل. اطلب من العميل إظهار رمز جديد من التطبيق.',
      methodNotAllowed: 'لا يمكن تسجيل الطلب بهذه الطريقة: يجب أن يوافق العميل بطريقة أخرى. راجع الطلب وأكّد مرة أخرى.',
      branchInactive: 'فرعك متوقف. اسأل المسؤول.',
      entryNotFound: 'لم يُعثر على هذا الطلب.',
      entryNotPending: 'هذا الطلب لم يعد في الانتظار — اكتمل أو رُفض أو أُلغي بالفعل.',
      entryExpired: 'انتهت مهلة هذا الطلب. ابدأه من جديد.',
      nothingToReverse: 'هذا الطلب لم يحرّك أي نقاط، فلا يوجد ما يُعكس.',
      orderAlreadyRecorded: 'رقم طلب نقطة البيع هذا مسجّل بالفعل في هذا الفرع.',
      staffUnassigned: 'لم يتم تعيينك إلى فرع. اطلب من أحد المسؤولين تعيينك.',
      customerNotFound: 'لا يوجد عميل مسجّل بهذا الرقم.',
      customerNotActive: 'حساب هذا العميل غير نشط.',
      insufficientPoints: 'لا يملك العميل نقاطًا كافية لهذا الخصم.',
      discountBelowMinimum: 'هذا الخصم أقل من الحد الأدنى المسموح به على هذا الطلب.',
      discountAboveMaximum: 'هذا الخصم أعلى من الحد الأقصى المسموح به على هذا الطلب.',
      otpInvalid: 'الرمز غير صحيح. اطلب من العميل قراءته مرة أخرى.',
      otpExpired: 'انتهت صلاحية الرمز المُرسل.',
      otpLocked: 'رموز خاطئة كثيرة. لم يعد بإمكان هذا الطلب إنفاق النقاط — ابدأ من جديد.',
      otpResendLimit: 'لا يمكن إرسال الرمز مرة أخرى.',
      network: 'تعذّر الوصول إلى الخدمة. تحقّق من الاتصال.',
      forbidden: 'ليست لديك صلاحية للقيام بذلك.',
      generic: 'حدث خطأ ما. حاول مرة أخرى، أو اسأل المشرف.',
    },
    settings: {
      title: 'إعدادات الكاشير',
      subtitle: 'الفروع، والفرع الذي يسجّل فيه كل موظف الطلبات.',
      branches: 'الفروع',
      addBranch: 'إضافة فرع',
      editBranch: 'تعديل الفرع',
      nameEn: 'الاسم (بالإنجليزية)',
      nameAr: 'الاسم (بالعربية)',
      kindBranch: 'فرع',
      kindHint: 'في مركز الاتصال، ينتظر إنفاق النقاط موافقة العميل في التطبيق.',
      codeHint: 'قصير ودائم، مثل ARENA. يُسجَّل على كل قيد في السجل من هذا الفرع.',
      codeRule: 'من 2 إلى 32 حرفًا إنجليزيًا كبيرًا أو رقمًا أو شرطة سفلية.',
      branchCreated: 'تمت إضافة الفرع',
      branchUpdated: 'تم تحديث الفرع',
      saveFailed: 'تعذّر حفظ الفرع.',
      loadFailed: 'تعذّر تحميل إعدادات الكاشير.',
      approval: 'موافقة العميل',
      requireAppApproval: 'طلب الموافقة في التطبيق',
      requireAppApprovalHint: 'أي طلب يستخدم نقاطًا أو يحصل على خصم العضوية ينتظر موافقة العميل في تطبيق Maky — في كل الفروع، وليس مركز الاتصال فقط. العميل الذي ليس لديه التطبيق يصله رمز برسالة بدلًا من ذلك. عند الإيقاف، يكفي رمز التطبيق لاستخدام النقاط في الفرع ويُطبَّق خصم العضوية دون سؤال.',
      approvalSaved: 'تم حفظ إعداد الموافقة',
      approvalSaveFailed: 'تعذّر حفظ إعداد الموافقة',
      noBranches: 'لا توجد فروع بعد',
      noBranchesHint: 'أضف الفروع ومركز الاتصال التي تستقبل الطلبات.',
      staff: 'الموظفون',
      staffHint: 'تُسجَّل الطلبات على الفرع المعيّن هنا، وليس على فرع يختاره الكاشير.',
      roles: 'الأدوار',
      branch: 'الفرع',
      branchFor: 'فرع {name}',
      noBranch: 'غير معيّن',
      noStaff: 'لا يوجد موظفون',
      noStaffHint: 'يظهر هنا المستخدمون الذين لديهم صلاحية الكاشير.',
      addBranchFirst: 'أضف فرعًا نشطًا قبل تعيين الموظفين.',
      assigned: 'تم حفظ التعيين',
      unassignedDetail: 'لم يعد {name} معيّنًا إلى أي فرع.',
      assignFailed: 'تعذّر حفظ التعيين',
    },
    report: {
      title: 'تقرير الكاشير',
      subtitle: 'يوم واحد عند الكاشير، لمطابقته مع تقرير نهاية اليوم في نظام نقاط البيع.',
      allBranches: 'كل الفروع',
      method: 'الطريقة',
      methods: {
        APP_CODE: 'رمز التطبيق',
        PHONE: 'الهاتف',
        SMS_OTP: 'الهاتف + رمز برسالة',
      },
      flags: 'تنبيهات',
      flag: {
        PHONE_REPEATED: 'هاتف متكرر',
        REFERENCE_REUSED_AFTER_REVERSAL: 'رقم نقطة البيع أُعيد استخدامه بعد العكس',
      },
      reversal: 'العكس',
      cashier: 'الكاشير',
      entries: 'الطلبات',
      discountGiven: 'الخصم الممنوح',
      pointsSpentHint: '{points} نقطة مُنفقة',
      exported: 'تم تصدير التقرير',
      emptyTitle: 'لا توجد طلبات في هذا اليوم',
      emptyHint: 'اختر تاريخًا أو فرعًا آخر.',
    },
  },

  memberships: {
    title: 'طلبات العضوية',
    subtitle: 'عملاء يتقدمون من التطبيق للانضمام إلى شريحة عضوية برقم العضوية وصورة الكارنيه. الموافقة تنقلهم إلى الشريحة فورًا.',
    submitted: 'تاريخ التقديم',
    membershipNumber: 'رقم العضوية',
    allSegments: 'كل الشرائح',
    emptyTitle: 'لا توجد طلبات هنا',
    emptyHint: 'تظهر هنا الطلبات المرسلة من التطبيق للمراجعة.',
    review: 'مراجعة',
    view: 'عرض',
    detailTitle: 'طلب عضوية',
    duplicatesTitle: 'تكرار محتمل: رقم العضوية هذا مستخدم في مكان آخر في هذه الشريحة ({count})',
    duplicatesHint: 'افحص الكارنيه جيدًا. وجود الرقم نفسه على حساب آخر قد يعني كارنيه مشتركًا أو مستعارًا.',
    applicationRef: 'الطلب رقم {id}',
    reviewed: 'تمت المراجعة',
    rejectReason: 'سبب الرفض',
    revoked: 'أُلغيت العضوية',
    revokeReason: 'سبب إلغاء العضوية',
    cardFront: 'الكارنيه — الوجه',
    cardBack: 'الكارنيه — الظهر',
    zoom: 'عرض بالحجم الكامل',
    cardLogged: 'لا تُفك صور الكارنيه إلا لعرضها لك، ويُسجَّل كل عرض.',
    readOnly: 'يمكنك عرض هذا الطلب دون البت فيه.',
    approve: 'موافقة',
    reject: 'رفض',
    approveTitle: 'الموافقة على هذه العضوية؟',
    approveMessage: 'سيُنقل {customer} إلى {segment} الآن وسيصله إشعار. يبدأ خصم العضوية من طلبه التالي.',
    approveDespiteDuplicates: 'رقم العضوية هذا مستخدم بالفعل في مكان آخر في هذه الشريحة ({count}). لا توافق إلا إذا كنت متأكدًا أن الكارنيه يخص هذا العميل.',
    rejectTitle: 'رفض هذا الطلب',
    rejectHint: 'يُرسل إلى العميل في التطبيق، فاكتبه موجهًا إليه.',
    approved: 'تمت الموافقة على العضوية',
    approvedDetail: 'العميل الآن في {segment}.',
    rejected: 'تم رفض الطلب',
    reasonRequired: 'اكتب سببًا من بضعة أحرف على الأقل.',
    card: {
      noBack: 'لم يرسل العميل ظهر الكارنيه.',
      shredded: 'حُذفت صورة الكارنيه هذه نهائيًا ولم يعد من الممكن عرضها.',
      notConfigured: 'لا يمكن عرض صور الكارنيه: مفتاح فك التشفير غير مُعد في الخدمة.',
      missing: 'صورة الكارنيه هذه غير متاحة.',
      failed: 'تعذر تحميل صورة الكارنيه.',
    },
    errors: {
      notFound: 'هذا الطلب لم يعد موجودًا.',
      notPending: 'تم البت في هذا الطلب أو سحبه بالفعل. حدّث الصفحة لمعرفة حالته.',
      alreadyMember: 'العميل عضو بالفعل في شريحة.',
      notMember: 'العميل ليس عضوًا عبر طلب مقبول، فلا توجد عضوية لإلغائها.',
      segmentInactive: 'هذه الشريحة لم تعد نشطة.',
      customerNotActive: 'العميل غير نشط.',
      customerNotFound: 'هذا العميل غير موجود.',
      reasonRequired: 'السبب مطلوب.',
      generic: 'تعذر تنفيذ ذلك. حاول مرة أخرى أو حدّث الصفحة.',
    },
  },

  noAccess: {
    title: 'لا توجد صلاحية',
    heading: 'ليس لديك صلاحية للوصول إلى أي شيء بعد',
    message: 'حسابك موجود، لكن لم يُسنَد إليه أي دور، لذا لا يوجد هنا ما يمكنك فتحه. يمكن للمسؤول إسناد دور من صفحة المستخدمين.',
    signedInAs: 'مسجّل الدخول باسم {username} ({email})',
  },

  notFound: {
    title: 'غير موجود',
    heading: 'هذه الصفحة غير موجودة',
    message: 'قد يكون الرابط قديمًا، أو ربما نُقلت الصفحة.',
    goHome: 'العودة إلى لوحة المعلومات',
  },
} as const
